import { useCallback, useState } from 'react';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { RoundVirtualAppointmentItem } from './RoundVirtualAppointmentItem';
import { VA_CARD_LIST_CLASS } from './VirtualAppointmentAccordionItem';
import { VirtualAppointmentDetailPrompt } from './VirtualAppointmentDetailPanels';
import { isOpenStatus } from './openVirtualAppointments';
import { defaultExpandedId, isRowExpanded, type FoldOverrides } from './roundFold';

const isOpenVa = (va: VirtualAppointmentDto) => isOpenStatus(va.status);

interface RoundLoadErrorProps {
    onRetry: () => void;
}

function RoundLoadError({ onRetry }: RoundLoadErrorProps) {
    return (
        <span role="alert" className="flex items-center gap-1.5 text-xs text-red-500">
            Couldn&apos;t load the full VA round; done VAs may be missing.
            <button
                type="button"
                onClick={onRetry}
                className="font-semibold underline hover:text-red-700"
            >
                Retry
            </button>
        </span>
    );
}

interface VirtualAppointmentWorkListProps {
    patientId: string;
    /** This round's VAs, open or done, in creation order. */
    round: VirtualAppointmentDto[];
    /** The patient's current aligner, when known; aligner snoozes must go past it. */
    currentAligner: number | null;
    totalAligners: number | null;
    /** UTC day the Treatment phase began; week snoozes and due days need it. */
    treatmentStartDate: string | null;
    /** Whether the patient is linked to Dental Monitoring; no-go snoozes need it. */
    dmLinked: boolean;
    /** Pass when the round start failed to load: the list shows the error and a retry. */
    onRetryRound?: () => void;
}

export function VirtualAppointmentWorkList({
    patientId,
    round,
    currentAligner,
    totalAligners,
    treatmentStartDate,
    dmLinked,
    onRetryRound
}: VirtualAppointmentWorkListProps) {
    const [overrides, setOverrides] = useState<FoldOverrides>({});
    const defaultId = defaultExpandedId(round, isOpenVa);
    const toggle = useCallback(
        (va: VirtualAppointmentDto) =>
            setOverrides((prev) => ({
                ...prev,
                [va.id]: { status: va.status, expanded: !isRowExpanded(va, prev, defaultId) }
            })),
        [defaultId]
    );

    return (
        <div className="flex flex-col">
            {onRetryRound && (
                <div className="px-4 pt-3">
                    <RoundLoadError onRetry={onRetryRound} />
                </div>
            )}
            {round.length === 0 ? (
                <VirtualAppointmentDetailPrompt message="No open VAs" />
            ) : (
                <div className={VA_CARD_LIST_CLASS}>
                    {round.map((va) => (
                        <RoundVirtualAppointmentItem
                            key={va.id}
                            patientId={patientId}
                            va={va}
                            currentAligner={currentAligner}
                            totalAligners={totalAligners}
                            treatmentStartDate={treatmentStartDate}
                            dmLinked={dmLinked}
                            expanded={isRowExpanded(va, overrides, defaultId)}
                            onToggle={() => toggle(va)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
